import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import Backend from "i18next-http-backend";
import {
  SUPPORTED_LANGUAGES,
  FALLBACK_LANGUAGE,
  NAMESPACES,
  DEFAULT_NAMESPACE,
  resolveInitialLanguage,
} from "./config";
import { useLocaleStore } from "../store/localeStore";

/**
 * Arranque de i18next para el portal REVI.
 *
 * Se importa el PRIMERO en `index.js`, antes de montar React: los componentes
 * piden traducciones en su primer render, asi que si i18next no esta listo
 * pintan la clave en bruto durante un instante.
 *
 * DOS COSAS QUE PARECEN DETALLE Y NO LO SON:
 *
 * 1. `loadPath` ARRANCA POR BARRA. Con una ruta relativa (`./locales/...`) y
 *    HashRouter —que es lo que usa este portal— el navegador resuelve la ruta
 *    contra la URL actual, no contra la raiz, y las traducciones dejan de
 *    cargar en cuanto se navega a una subruta. Esta aprendido en K12 y viene
 *    documentado en su propio codigo.
 *
 * 2. El idioma inicial sale del store y pasa por `resolveInitialLanguage`, que
 *    descarta un idioma persistido que ya no este disponible. Sin eso, quien
 *    probo un idioma que despues se apaga se queda viendolo.
 */
const idiomaInicial = resolveInitialLanguage(useLocaleStore.getState().locale);

i18n
  .use(Backend)
  .use(initReactI18next)
  .init({
    lng: idiomaInicial,
    fallbackLng: FALLBACK_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    defaultNS: DEFAULT_NAMESPACE,
    ns: NAMESPACES,
    backend: {
      // CRITICO: ruta absoluta. Relativa se rompe con HashRouter.
      //
      // Y CRITICO EL `?v=`. Firebase sirve estos ficheros con
      // `Cache-Control: max-age=3600` y su URL es estable, al reves que el
      // bundle de JS, que cambia de hash en cada build. Sin el parametro, un
      // navegador que cargo los locales hace menos de una hora sigue usando los
      // viejos: la pantalla nueva pide claves que el fichero cacheado no tiene
      // y el usuario ve `resumenIa.titulo` en crudo.
      //
      // Paso de verdad el 2026-08-10, y no es un caso raro: le pasa a
      // CUALQUIERA que tuviera el portal abierto en la ultima hora, cada vez
      // que se toca una traduccion.
      //
      // El valor es el SHA del commit, que inyecta el workflow de despliegue.
      // En local no esta definido y vale "dev": ahi el servidor de desarrollo
      // no cachea, asi que no hace falta.
      loadPath: `/locales/{{lng}}/{{ns}}.json?v=${
        process.env.REACT_APP_I18N_VERSION || "dev"
      }`,
    },
    interpolation: {
      // React ya escapa lo que pinta; volver a escapar aqui duplicaria las
      // entidades HTML y se verian como texto.
      escapeValue: false,
    },
    react: {
      useSuspense: true,
    },
  });

// El store manda: si alguien cambia el idioma, i18next lo sigue. Asi el
// selector no tiene que conocer a i18next y solo habla con el store.
useLocaleStore.subscribe((estado, anterior) => {
  if (estado.locale !== anterior.locale && estado.locale !== i18n.language) {
    i18n.changeLanguage(estado.locale);
  }
});

// Y al reves: si el idioma persistido ya no vale, el store se corrige para que
// la cabecera Accept-Language no arrastre uno retirado.
if (useLocaleStore.getState().locale !== idiomaInicial) {
  useLocaleStore.getState().setLocale(idiomaInicial);
}

export default i18n;
